/**
 * 하단 대화창. NPC 앞에서 ↑ 로 연다.
 */

import Phaser from 'phaser';
import { Sfx } from '../audio/Sfx';
import { getNpc } from '../data/npcs';
import { getQuest } from '../data/quests';
import { EV } from '../game/events';
import { pickNpcQuest, questPhase } from '../systems/questLogic';
import { nextJobOptions, requiredLevel } from '../systems/jobAdvance';
import type { PlayerState } from '../systems/PlayerState';
import { COLOR, TEXT_COLOR, UI_FONT } from './theme';

const WIDTH = 760;
const HEIGHT = 168;

export class DialogOverlay {
  private readonly container: Phaser.GameObjects.Container;
  private readonly nameText: Phaser.GameObjects.Text;
  private readonly bodyText: Phaser.GameObjects.Text;
  private readonly hintText: Phaser.GameObjects.Text;
  private npcId: string | null = null;
  private lineIndex = 0;
  private mode: 'talk' | 'quest' | 'menu' | 'advance' = 'talk';
  private questId: string | null = null;

  constructor(
    scene: Phaser.Scene,
    private readonly state: PlayerState,
  ) {
    const x = (scene.scale.width - WIDTH) / 2;
    const y = scene.scale.height - HEIGHT - 92;
    this.container = scene.add.container(x, y).setVisible(false).setDepth(40);

    const g = scene.add.graphics();
    g.fillStyle(COLOR.panelEdge, 0.96);
    g.fillRoundedRect(-4, -4, WIDTH + 8, HEIGHT + 8, 12);
    g.fillStyle(COLOR.window, 0.98);
    g.fillRoundedRect(0, 0, WIDTH, HEIGHT, 10);
    this.container.add(g);

    this.nameText = scene.add.text(18, 12, '', {
      fontFamily: UI_FONT,
      fontSize: '16px',
      color: TEXT_COLOR.dark,
      wordWrap: { width: WIDTH - 36, useAdvancedWrap: true },
    });
    this.bodyText = scene.add.text(18, 42, '', {
      fontFamily: UI_FONT,
      fontSize: '15px',
      color: TEXT_COLOR.dark,
      wordWrap: { width: WIDTH - 36 },
    });
    this.hintText = scene.add.text(WIDTH - 18, HEIGHT - 22, '', {
      fontFamily: UI_FONT,
      fontSize: '12px',
      color: TEXT_COLOR.darkDim,
      wordWrap: { width: WIDTH - 36, useAdvancedWrap: true },
      align: 'right',
    });
    this.hintText.setOrigin(1, 1);
    this.container.add([this.nameText, this.bodyText, this.hintText]);
  }

  get visible(): boolean {
    return this.container.visible;
  }

  open(npcId: string): void {
    const npc = getNpc(npcId);
    if (!npc) return;
    this.npcId = npcId;
    this.lineIndex = 0;
    this.mode = 'talk';
    this.questId = null;
    this.container.setVisible(true);
    this.refresh();
  }

  close(): void {
    this.container.setVisible(false);
    this.npcId = null;
  }

  handleKey(code: string): boolean {
    if (!this.visible) return false;
    if (code === 'ESC') {
      this.close();
      return true;
    }
    if (this.mode === 'talk') {
      if (code === 'ENTER' || code === 'SPACE') {
        this.advanceTalk();
        return true;
      }
      return true;
    }
    if (this.mode === 'menu') {
      if (code === 'ONE') {
        this.enterQuest();
        return true;
      }
      if (code === 'TWO') {
        this.openShop();
        return true;
      }
      if (code === 'ENTER' || code === 'SPACE') {
        this.close();
        return true;
      }
      return true;
    }
    if (this.mode === 'advance') {
      return this.handleAdvanceKey(code);
    }
    if (this.mode === 'quest' && this.questId) {
      const phase = questPhase(this.questId, this.state.quests, this.state.inventory);
      if ((code === 'Y' || code === 'ENTER') && phase === 'offer') {
        this.state.acceptQuest(this.questId);
        this.refresh();
        return true;
      }
      if ((code === 'Y' || code === 'ENTER') && phase === 'ready') {
        this.state.completeQuest(this.questId);
        this.refresh();
        return true;
      }
      if (code === 'N' || code === 'ENTER' || code === 'SPACE') {
        this.close();
        return true;
      }
    }
    return true;
  }

  private advanceTalk(): void {
    const npc = this.npcId ? getNpc(this.npcId) : undefined;
    if (!npc) return;
    this.lineIndex += 1;
    if (this.lineIndex < npc.lines.length) {
      this.refresh();
      return;
    }
    const hasQuest = Boolean(npc.questIds?.length);
    const hasShop = Boolean(npc.shopId);
    if (hasQuest && hasShop) {
      this.mode = 'menu';
      this.refresh();
      return;
    }
    if (hasQuest) {
      this.enterQuest();
      return;
    }
    if (hasShop) {
      this.openShop();
      return;
    }
    if (npc.jobAdvance) {
      this.mode = 'advance';
      this.refresh();
      return;
    }
    this.close();
  }

  private enterQuest(): void {
    const npc = this.npcId ? getNpc(this.npcId) : undefined;
    if (!npc) return;
    const pick = pickNpcQuest(npc.questIds, this.state.quests, this.state.inventory);
    if (!pick) {
      this.bodyText.setText('지금은 부탁할 일이 없어요.');
      this.hintText.setText('Enter 닫기');
      this.mode = 'talk';
      this.lineIndex = npc.lines.length;
      return;
    }
    this.mode = 'quest';
    this.questId = pick.id;
    this.refresh();
  }

  private openShop(): void {
    const npc = this.npcId ? getNpc(this.npcId) : undefined;
    if (!npc?.shopId) return;
    Sfx.uiClick();
    this.close();
    this.state.events.emit(EV.shopOpen, { shopId: npc.shopId });
  }

  private refresh(): void {
    const npc = this.npcId ? getNpc(this.npcId) : undefined;
    if (!npc) return;
    this.nameText.setText(`${npc.name}  ·  ${npc.title}`);
    if (this.mode === 'talk') {
      this.bodyText.setText(npc.lines[this.lineIndex] ?? '');
      this.hintText.setText('Enter 다음');
      return;
    }
    if (this.mode === 'menu') {
      this.bodyText.setText('무엇을 하시겠어요?\n1  퀘스트    2  상점');
      this.hintText.setText('Esc 닫기');
      return;
    }
    if (this.mode === 'advance') {
      this.refreshAdvance();
      return;
    }
    if (!this.questId) return;
    const quest = getQuest(this.questId);
    const phase = questPhase(this.questId, this.state.quests, this.state.inventory);
    const title = quest ? `[${quest.name}] ` : '';
    this.bodyText.setText(title + this.state.questLine(this.questId));
    if (phase === 'offer') this.hintText.setText('Y 수락  ·  N 거절');
    else if (phase === 'ready') this.hintText.setText('Y 완료  ·  Esc 닫기');
    else this.hintText.setText('Enter 닫기');
  }

  private handleAdvanceKey(code: string): boolean {
    const options = nextJobOptions(this.state.jobId);
    const req = requiredLevel(this.state.jobId);
    if (req === null || options.length === 0) {
      if (code === 'ENTER' || code === 'SPACE') this.close();
      return true;
    }
    if (this.state.level < req) {
      if (code === 'ENTER' || code === 'SPACE') this.close();
      return true;
    }
    if (options.length === 1 && (code === 'Y' || code === 'ENTER')) {
      this.confirmAdvance(options[0].id);
      return true;
    }
    const pick =
      code === 'ONE' ? options[0] : code === 'TWO' ? options[1] : code === 'THREE' ? options[2] : undefined;
    if (pick) this.confirmAdvance(pick.id);
    return true;
  }

  private confirmAdvance(nextId: string): void {
    if (!this.state.advanceJob(nextId)) return;
    Sfx.uiClick();
    this.close();
  }

  private refreshAdvance(): void {
    const options = nextJobOptions(this.state.jobId);
    const req = requiredLevel(this.state.jobId);
    if (req === null || options.length === 0) {
      this.bodyText.setText('더 이상 전직할 직업이 없습니다. 4차까지 마치셨군요.');
      this.hintText.setText('Enter 닫기');
      return;
    }
    if (this.state.level < req) {
      this.bodyText.setText(
        `지금은 레벨이 부족합니다. 다음 전직은 레벨 ${req} 이상이어야 합니다. (현재 ${this.state.level})`,
      );
      this.hintText.setText('Enter 닫기');
      return;
    }
    if (options.length === 1) {
      this.bodyText.setText(`${options[0].name}(으)로 전직하시겠습니까?`);
      this.hintText.setText('Y 전직  ·  Esc 닫기');
      return;
    }
    const lines = options.map((job, i) => `${i + 1}  ${job.name}`).join('    ');
    this.bodyText.setText(`어느 직업으로 전직하시겠습니까?\n${lines}`);
    this.hintText.setText('숫자 선택  ·  Esc 닫기');
  }
}
